import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from 'react-hot-toast';
import { Slider } from "../../components/ui/slider";
import { Check, AlertTriangle, Award, Users, Star, Trophy, RefreshCw, MessageSquare, Clock } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from 'src/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { formatMotion } from '../../utils/format-helpers';
import RatingEventBus from '../../services/RatingEventBus';
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

// TimeInput component for better speaking time input
interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  maxMinutes?: number;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, maxMinutes = 8 }) => {
  // Parse the current value into minutes and seconds
  const parseTime = (timeString: string) => {
    if (!timeString) return { minutes: 0, seconds: 0 };
    
    const parts = timeString.split(':');
    if (parts.length !== 2) return { minutes: 0, seconds: 0 };
    
    return {
      minutes: parseInt(parts[0], 10) || 0,
      seconds: parseInt(parts[1], 10) || 0
    };
  };
  
  const { minutes, seconds } = parseTime(value);
  
  // Check if time exceeds maximum
  const isOverLimit = minutes > maxMinutes || (minutes === maxMinutes && seconds > 0);
  
  // Generate options for minutes and seconds dropdowns
  const minuteOptions = Array.from({ length: maxMinutes + 1 }, (_, i) => i);
  const secondOptions = Array.from({ length: 60 }, (_, i) => i);
  
  // Handle changes to minutes or seconds
  const handleMinutesChange = (newMinutes: string) => {
    // Ensure we don't exceed max minutes
    const validMinutes = Math.min(parseInt(newMinutes, 10), maxMinutes);
    const formattedTime = `${String(validMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    onChange(formattedTime);
  };
  
  const handleSecondsChange = (newSeconds: string) => {
    const formattedTime = `${String(minutes).padStart(2, '0')}:${String(newSeconds).padStart(2, '0')}`;
    onChange(formattedTime);
  };
  
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <div className="flex items-center">
          <Clock size={14} className="mr-1 text-gray-500" />
          <Select value={String(minutes)} onValueChange={handleMinutesChange}>
            <SelectTrigger className="w-[60px]">
              <SelectValue placeholder="Min" />
            </SelectTrigger>
            <SelectContent>
              {minuteOptions.map((min) => (
                <SelectItem key={`min-${min}`} value={String(min)}>
                  {String(min).padStart(2, '0')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-gray-500">:</span>
        <Select value={String(seconds)} onValueChange={handleSecondsChange}>
          <SelectTrigger className="w-[60px]">
            <SelectValue placeholder="Sec" />
          </SelectTrigger>
          <SelectContent>
            {secondOptions.map((sec) => (
              <SelectItem key={`sec-${sec}`} value={String(sec)}>
                {String(sec).padStart(2, '0')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {isOverLimit && (
        <p className="text-xs text-red-500">
          Time exceeds 8 minute limit
        </p>
      )}
    </div>
  );
};

interface RatingStatus {
  participants: {
    debaters: Array<{
      user_id: number;
      username: string;
      role: string;
      has_submitted_ratings: boolean;
    }>;
    adjudicators: Array<{
      user_id: number;
      username: string;
      role: string;
      has_submitted_ratings: boolean;
    }>;
    spectators: Array<{
      user_id: number;
      username: string;
      role: string;
      has_submitted_ratings: boolean;
    }>;
  };
  all_ratings_submitted: boolean;
  debaters_rated: boolean;
  panelists_rated_chair: boolean;
  chair_adjudicator: {
    user_id: number;
    username: string;
    role: string;
  } | null;
}

interface AdjudicatorViewProps {
  roundData: any;
  userRole: string;
  roundId: string;
}
const AdjudicatorView: React.FC<AdjudicatorViewProps> = ({ roundData, userRole, roundId }) => {
  // Initialize navigate function from React Router
  const navigate = useNavigate();
  
  // Component state
  const [speakerScores, setSpeakerScores] = useState<{ [key: number]: number }>({});
  const [adjudicatorRatings, setAdjudicatorRatings] = useState<{ [key: number]: number }>({});
  const [winningSide, setWinningSide] = useState<string>("");
  const [teamRankings, setTeamRankings] = useState<{ [team: string]: number }>({});
  const [scoresModified, setScoresModified] = useState(false);
  const [ratingsModified, setRatingsModified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ratingStatus, setRatingStatus] = useState<RatingStatus | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("adjudication");
  const [speakingTimes, setSpeakingTimes] = useState<{ [key: number]: string }>({});
  const [speakerComments, setSpeakerComments] = useState<{ [key: number]: string }>({});
  const [roundSummary, setRoundSummary] = useState<string>("Round completed successfully");

  // Component ID for tracking
  const componentId = `adjudicator-view-${roundId}`;

  // Fix useEffect with missing componentId dependency
  useEffect(() => {
    // Register component
    RatingEventBus.registerComponent(componentId);

    // This will handle rating submission events
    const ratingSubmissionHandler = (data: any) => {
      // Only process events for this round and if component is mounted
      if (data.roundId !== roundId && data.roundId !== parseInt(roundId)) {
        return;
      }

      console.log(`Rating submission received from ${data.username} (${data.role})`);

      // Show role-specific toast messages
      if (data.isPanelist) {
        toast.success(`${data.username} submitted Chair Adjudicator rating`, {
          icon: '⭐',
          duration: 4000
        });
      } else if (data.isDebater) {
        toast.success(`${data.username} submitted adjudicator ratings`, {
          icon: '📊',
          duration: 3000
        });
      }

      // Check if all submissions are complete
      const currentStatus = RatingEventBus.getRatingStatus(roundId);
      if (currentStatus?.all_ratings_submitted && ratingStatus && !ratingStatus.all_ratings_submitted) {
        toast.success('All participants have submitted their ratings! You can now finalize the round.', {
          duration: 5000,
          icon: '✅'
        });
      }
    };

    // This will receive status data from the status bus
    const statusDataHandler = (data: any) => {
      if (data.roundId === roundId || data.roundId === parseInt(roundId)) {
        setRatingStatus(data);
      }
    };

    // Subscribe to events
    const unsubscribeRating = RatingEventBus.subscribe('RATING_SUBMITTED', ratingSubmissionHandler, componentId);
    const unsubscribeStatus = RatingEventBus.subscribe('STATUS_UPDATED', statusDataHandler, componentId);

    return () => {
      // Cleanup subscriptions
      unsubscribeRating();
      unsubscribeStatus();
      RatingEventBus.unregisterComponent(componentId);
    };
  }, [roundId, ratingStatus, componentId]);

  // Get current user ID
  const getCurrentUserId = () => {
    return roundData.current_user_id;
  };

  // Add a handler for speaking time changes
  const handleSpeakingTimeChange = (speakerId: number, value: string) => {
    // Store the formatted time value directly
    setSpeakingTimes({
      ...speakingTimes,
      [speakerId]: value
    });
    setScoresModified(true);
  };

  // Add a handler for speaker comments changes
  const handleSpeakerCommentChange = (speakerId: number, value: string) => {
    setSpeakerComments({
      ...speakerComments,
      [speakerId]: value
    });
    setScoresModified(true);
  };

  // Add a handler for round summary changes
  const handleRoundSummaryChange = (value: string) => {
    setRoundSummary(value);
    setScoresModified(true);
  };

  // Submit adjudication
  const submitAdjudication = async () => {
    try {
      // Validate speaking times before submission
      const invalidTimes = Object.entries(speakingTimes).filter(([_, time]) => {
        if (!time) return false; // Empty times are valid
        
        const parts = time.split(':');
        if (parts.length !== 2) return true; // Invalid format
        
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);
        
        return minutes > 8 || (minutes === 8 && seconds > 0);
      });
      
      if (invalidTimes.length > 0) {
        toast.error('Speaking time cannot exceed 8 minutes');
        return;
      }
      
      setSubmitting(true);

      // Create speaker scores array
      const speaker_scores = getDebaters().map((debater: any) => {
        return {
          speaker_id: debater.user.id, // Use the actual user ID, not the allocation ID
          score: speakerScores[debater.id] || 67,
          comments: speakerComments[debater.id] || "",
          speaking_time: speakingTimes[debater.id] || null
        };
      });

      // Create adjudicator ratings array
      const ratings = getRateableAdjudicators().map((adjudicator: any) => {
        return {
          adjudicator_id: adjudicator.user.id, // Use the actual user ID, not the allocation ID
          rating: adjudicatorRatings[adjudicator.id] || 1
        };
      });

      // Submit speaker scores first
      console.log("Submitting speaker scores:", speaker_scores);
      await fetchWithAuth(`/api/round/${roundId}/submit-speaker-scores/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ speaker_scores })
      });
      console.log("Speaker scores submitted successfully");

      // Submit adjudicator ratings
      console.log("Submitting adjudicator ratings:", ratings);
      await fetchWithAuth(`/api/round/${roundId}/submit-adjudicator-ratings/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ratings })
      });
      console.log("Adjudicator ratings submitted successfully");

      // Create speaker comments and speaking times objects for adjudication
      const speaker_comments: { [key: string]: string } = {};
      const speaking_times: { [key: string]: string } = {};
      
      getDebaters().forEach((debater: any) => {
        if (speakerComments[debater.id]) {
          speaker_comments[debater.user.id] = speakerComments[debater.id];
        }
        if (speakingTimes[debater.id]) {
          speaking_times[debater.user.id] = speakingTimes[debater.id];
        }
      });

      // Submit adjudication with comments, speaking times, and summary
      console.log("Submitting adjudication:", { 
        winning_side: winningSide,
        summary: roundSummary,
        speaker_comments,
        speaking_times
      });
      
      await fetchWithAuth(`/api/round/${roundId}/adjudication/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          winning_side: winningSide,
          team_rankings: roundData.format === 'BP' || roundData.format === 'ABP' ? teamRankings : null,
          summary: roundSummary,
          speaker_comments,
          speaking_times
        })
      });

      // Mark submission in EventBus
      RatingEventBus.markUserSubmitted(
        roundId, 
        roundData.current_user_id, 
        roundData.current_username, 
        userRole
      );

      toast.success('Adjudication submitted successfully');
      setHasSubmitted(true);
      setScoresModified(false);
      setRatingsModified(false);

    } catch (error) {
      console.error("Error submitting adjudication:", error);
      toast.error(`Error submitting adjudication: ${error}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Finalize round
  const finalizeRound = async () => {
    try {
      if (!winningSide) {
        toast.error('Please select a winning side before finalizing the round');
        return;
      }

      console.log("Finalizing round with payload:", { winning_side: winningSide });
      console.log("Finalizing round via:", `/api/round/${roundId}/finalize/`);

      const response = await fetchWithAuth(`/api/round/${roundId}/finalize/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          winning_side: winningSide
        })
      });

      toast.success('Round finalized successfully');
      
      // Redirect to results page after a short delay
      setTimeout(() => {
        navigate(`/round/${roundId}/results`);
      }, 1500);

    } catch (error) {
      console.error("Error finalizing round:", error);
      toast.error(`Error finalizing round: ${error}`);
    }
  };

  // Add manual refresh button in DetailedRatingStatusView
  const handleManualRefresh = () => {
    toast.success('Refreshing rating status...', {
      icon: '🔄',
      duration: 2000
    });

    RatingEventBus.publish('MANUAL_REFRESH_REQUESTED', {
      roundId,
      timestamp: new Date().toISOString()
    });
  };

  // Get all debaters (speakers)
  const getDebaters = () => {
    const debaterRoles = [
      'Prime Minister', 'Deputy Prime Minister', 'Government Whip', 'Member of Government',
      'Leader of Opposition', 'Deputy Leader of Opposition', 'Opposition Whip', 'Member of Opposition'
    ];

    return roundData.participants.filter((p: any) => debaterRoles.includes(p.role));
  };

  // Get other adjudicators that can be rated
  const getRateableAdjudicators = () => {
    // Chair can rate panelists
    return roundData.participants.filter((p: any) =>
      ['Panelist'].includes(p.role) && p.user_id !== getCurrentUserId()
    );
  };

  // Handle speaker score change
  const handleSpeakerScoreChange = (speakerId: number, value: number[]) => {
    setSpeakerScores({
      ...speakerScores,
      [speakerId]: value[0]
    });
    setScoresModified(true);
  };

  // Handle adjudicator rating change
  const handleAdjudicatorRatingChange = (adjudicatorId: number, value: number[]) => {
    setAdjudicatorRatings({
      ...adjudicatorRatings,
      [adjudicatorId]: value[0]
    });
    setRatingsModified(true);
  };

  // Handle team ranking change (for BP)
  const handleTeamRankingChange = (teamName: string, rank: number) => {
    setTeamRankings({
      ...teamRankings,
      [teamName]: rank
    });
    setScoresModified(true);
  };

  // Get team names for BP format
  const getTeamNames = () => {
    return ['Opening Government', 'Opening Opposition', 'Closing Government', 'Closing Opposition'];
  };

  // Check if the user can submit final results
  const canSubmitFinalResults = userRole === 'Chair Adjudicator';

  // A detailed Rating Status Component for Chair Adjudicator
  const DetailedRatingStatusView = () => {
    // If no rating status is available, show loading view
    if (!ratingStatus) {
      return (
        <Card className="mb-4">
          <CardContent className="pt-4 flex flex-col items-center justify-center p-6">
            <div className="flex flex-col items-center space-y-4">
              <Star className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-500 text-center">
                Rating status information is currently unavailable. Try refreshing in a few moments.
              </p>
              <Button
                onClick={handleManualRefresh}
                variant="outline"
                size="sm"
                className="mt-4"
              >
                <RefreshCw size={14} className="mr-2" />
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium flex items-center">
                <Star className="mr-2" size={18} />
                Rating Status Overview
              </h3>
              <Button 
                onClick={handleManualRefresh} 
                variant="outline"
                size="sm"
              >
                <RefreshCw size={14} className="mr-2" />
                Refresh Status
              </Button>
            </div>
            
            {/* Status banner showing if all ratings are submitted */}
            {ratingStatus.all_ratings_submitted ? (
              <div className="p-4 bg-green-100 dark:bg-green-900 dark:bg-opacity-20 border border-green-500 rounded-lg">
                <div className="flex items-center">
                  <Check size={20} className="text-green-500 mr-2" />
                  <p className="text-green-700 dark:text-green-400 font-medium">All participants have submitted their ratings</p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-amber-100 dark:bg-amber-900 dark:bg-opacity-20 border border-amber-500 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle size={20} className="text-amber-500 mr-2" />
                  <p className="text-amber-700 dark:text-amber-400 font-medium">Some participants have not submitted their ratings</p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Round results cannot be finalized until all required ratings have been submitted.
                </p>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Debaters Section - FIXED to only include actual debaters */}
              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-medium mb-3 flex items-center">
                    <Users size={16} className="mr-2" />
                    Debaters
                  </h4>

                  <div className="space-y-2">
                    {ratingStatus.participants.debaters
                      // Filter to ensure only real debaters are here (not panelists)
                      .filter(debater => !debater.role.includes('Adjudicator'))
                      .map((debater) => (
                        <div key={debater.user_id} className="flex items-center justify-between p-2 border rounded-md">
                          <div>
                            <span className="font-medium">{debater.username}</span>
                            <span className="text-gray-500 text-xs ml-2">({debater.role})</span>
                          </div>
                          <Badge
                            variant={debater.has_submitted_ratings ? "default" : "outline"}
                            className={debater.has_submitted_ratings ? "bg-green-500 hover:bg-green-600" : ""}
                          >
                            {debater.has_submitted_ratings ? 'Submitted' : 'Pending'}
                          </Badge>
                        </div>
                      ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-medium">Status:</span>
                    {ratingStatus.debaters_rated ? (
                      <Badge variant="default" className="bg-green-500">
                        Complete
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-500 border-amber-500">
                        Incomplete
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Adjudicators Section - FIXED to include panelists */}
              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-medium mb-3 flex items-center">
                    <Award size={16} className="mr-2" />
                    Adjudicators
                  </h4>

                  <div className="space-y-2">
                    {/* Get all panelists from ratingStatus */}
                    {ratingStatus.participants.adjudicators
                      .filter(adj => adj.role === 'Panelist')
                      .map((adjudicator) => (
                        <div key={adjudicator.user_id} className="flex items-center justify-between p-2 border rounded-md">
                          <div>
                            <span className="font-medium">{adjudicator.username}</span>
                            <span className="text-gray-500 text-xs ml-2">({adjudicator.role})</span>
                          </div>
                          <Badge
                            variant={adjudicator.has_submitted_ratings ? "default" : "outline"}
                            className={adjudicator.has_submitted_ratings ? "bg-green-500 hover:bg-green-600" : ""}
                          >
                            {adjudicator.has_submitted_ratings ? 'Submitted' : 'Pending'}
                          </Badge>
                        </div>
                      ))}

                    {/* Display any other adjudicators that might have been miscategorized */}
                    {ratingStatus.participants.debaters
                      .filter(participant => participant.role.includes('Adjudicator') && participant.role !== 'Chair Adjudicator')
                      .map((adjudicator) => (
                        <div key={adjudicator.user_id} className="flex items-center justify-between p-2 border rounded-md">
                          <div>
                            <span className="font-medium">{adjudicator.username}</span>
                            <span className="text-gray-500 text-xs ml-2">({adjudicator.role})</span>
                          </div>
                          <Badge
                            variant={adjudicator.has_submitted_ratings ? "default" : "outline"}
                            className={adjudicator.has_submitted_ratings ? "bg-green-500 hover:bg-green-600" : ""}
                          >
                            {adjudicator.has_submitted_ratings ? 'Submitted' : 'Pending'}
                          </Badge>
                        </div>
                      ))}

                    {ratingStatus.chair_adjudicator && (
                      <div className="flex items-center justify-between p-2 border rounded-md bg-purple-50 dark:bg-purple-900 dark:bg-opacity-20">
                        <div>
                          <span className="font-medium">{ratingStatus.chair_adjudicator.username}</span>
                          <span className="text-purple-500 text-xs ml-2">(Chair Adjudicator)</span>
                        </div>
                        <Badge variant="secondary" className="bg-purple-200 text-purple-700 dark:bg-purple-800 dark:text-purple-200">
                          N/A
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-medium">Status:</span>
                    {ratingStatus.panelists_rated_chair ? (
                      <Badge variant="default" className="bg-green-500">
                        Complete
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-500 border-amber-500">
                        Incomplete
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6">
              <Card className="border-t-4 border-t-blue-500">
                <CardContent className="pt-4">
                  <h4 className="font-medium mb-2">What's Required:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>All debaters must rate the adjudicator panel</li>
                    <li>All panelists must rate the chair adjudicator</li>
                    <li>Chair adjudicator must submit speaker scores</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Final Results Button for Chair */}
        {canSubmitFinalResults && (
          <Button
            onClick={finalizeRound}
            disabled={submitting || !(ratingStatus?.all_ratings_submitted)}
            className={`w-full ${ratingStatus?.all_ratings_submitted
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-gray-400 cursor-not-allowed'}`}
            title={ratingStatus?.all_ratings_submitted
              ? 'Finalize round results'
              : 'All participants must submit ratings before finalizing'}
          >
            <Check size={16} className="mr-2" />
            Finalize Round Results
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* User role info */}
      <div className="p-4 border-2 border-purple-500 bg-purple-900 bg-opacity-10 rounded-lg">
        <h3 className="font-semibold mb-2">{userRole}</h3>
        <p className="text-sm">
          As Chair Adjudicator, you can rate speakers, other adjudicators, and submit the final round results.
        </p>
      </div>

      {/* Motion Section */}
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Motion</h3>
        <p className="dark:text-white light:text-gray-800">{formatMotion(roundData.motion)}</p>
      </div>

      {/* Use tabs for Chair Adjudicator */}
      <Tabs defaultValue="adjudication" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="adjudication" className="text-sm">Adjudication</TabsTrigger>
          <TabsTrigger value="status" className="text-sm">
            Rating Status
            {ratingStatus && !ratingStatus.all_ratings_submitted && (
              <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-medium text-white">
                !
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="adjudication" className="space-y-4 mt-4">
          {/* All speaker scores, ratings and other elements */}
          <div className="space-y-4">
            {/* Speaker scores section */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center">
                <Users className="mr-2" size={18} />
                Speaker Scores
                <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded px-2 py-1 ml-2">
                  Range: 67-83
                </span>
              </h3>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Speaker</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Score (67-83)</TableHead>
                    <TableHead>Speaking Time</TableHead>
                    <TableHead>Comments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getDebaters().map((debater: any) => (
                    <TableRow key={debater.id}>
                      <TableCell className="font-medium">{debater.user.username}</TableCell>
                      <TableCell>{debater.role}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Score: {speakerScores[debater.id] || 67}</span>
                          </div>
                          <Slider
                            defaultValue={[67]}
                            min={67}
                            max={83}
                            step={1}
                            onValueChange={(value) => handleSpeakerScoreChange(debater.id, value)}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <TimeInput
                          value={speakingTimes[debater.id] || ''}
                          onChange={(value) => handleSpeakingTimeChange(debater.id, value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea
                          placeholder="Comments"
                          value={speakerComments[debater.id] || ''}
                          onChange={(e) => handleSpeakerCommentChange(debater.id, e.target.value)}
                          className="w-full h-20 resize-none text-sm"
                          autoResize={true}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Round Summary Section */}
            <div className="space-y-4 mt-6">
              <h3 className="font-semibold flex items-center">
                <MessageSquare className="mr-2" size={18} />
                Round Summary
              </h3>
              <Textarea
                placeholder="Provide a summary of the round"
                value={roundSummary}
                onChange={(e) => handleRoundSummaryChange(e.target.value)}
                className="w-full h-32 resize-none"
                autoResize={true}
              />
              <p className="text-xs text-gray-500">
                This summary will be visible to all participants in the round results page
              </p>
            </div>

            {/* Adjudicator ratings section */}
            {getRateableAdjudicators().length > 0 && !hasSubmitted && (
              <div className="space-y-4 mt-6">
                <h3 className="font-semibold flex items-center">
                  <Star className="mr-2" size={18} />
                  Rate Adjudicators
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {getRateableAdjudicators().map((adjudicator: any) => (
                    <Card key={adjudicator.id} className="p-3 border">
                      <div className="flex flex-col gap-4">
                        <div>
                          <p className="font-medium">{adjudicator.user.username}</p>
                          <p className="text-xs text-gray-400">{adjudicator.role}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="text-amber-500" size={16} />
                            <p className="text-sm">Rating: {adjudicatorRatings[adjudicator.id] || 1}</p>
                          </div>
                          <Slider
                            defaultValue={[1]}
                            min={1}
                            max={10}
                            step={1}
                            onValueChange={(value) => handleAdjudicatorRatingChange(adjudicator.id, value)}
                          />
                          <div className="flex justify-between mt-1">
                            <span className="text-xs text-gray-400">Poor (1)</span>
                            <span className="text-xs text-gray-400">Excellent (10)</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* BP Format Team Rankings */}
            {(roundData.format === 'BP' || roundData.format === 'ABP') && (
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-medium mb-4 flex items-center">
                    <Trophy className="mr-2" size={20} />
                    Team Rankings (BP Format)
                  </h3>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Team</TableHead>
                        <TableHead>Rank</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getTeamNames().map((teamName) => (
                        <TableRow key={teamName}>
                          <TableCell className="font-medium">{teamName}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-2">
                              <div className="flex justify-between">
                                <span className="text-sm">Rank: {teamRankings[teamName] || 1}</span>
                              </div>
                              <Slider
                                defaultValue={[1]}
                                min={1}
                                max={4}
                                step={1}
                                onValueChange={(value) => handleTeamRankingChange(teamName, value[0])}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Winner Selection UI */}
            {roundData.format !== 'BP' && roundData.format !== 'ABP' && (
              <Card className="mb-4 border-green-500 bg-green-900 bg-opacity-5">
                <CardContent className="p-4">
                  <h3 className="font-medium mb-4 flex items-center">
                    <Award className="mr-2 text-green-500" size={20} />
                    Select the Winning Side
                  </h3>

                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={() => setWinningSide('GOVERNMENT')}
                        className={`flex-1 ${winningSide === 'GOVERNMENT'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                      >
                        Government
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setWinningSide('OPPOSITION')}
                        className={`flex-1 ${winningSide === 'OPPOSITION'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                      >
                        Opposition
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 italic">
                      The winning side will be recorded in the round results.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit buttons */}
            <div className="flex flex-col md:flex-row gap-3 mt-6">
              {!hasSubmitted && (
                <Button
                  onClick={submitAdjudication}
                  disabled={submitting || (!scoresModified && !ratingsModified)}
                  className="flex-1 md:flex-none"
                >
                  Submit Speaker Scores & Ratings
                </Button>
              )}

              <Button
                onClick={finalizeRound}
                disabled={submitting || !(ratingStatus?.all_ratings_submitted)}
                className={`flex-1 md:flex-none ${ratingStatus?.all_ratings_submitted
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-400 cursor-not-allowed'}`}
                title={ratingStatus?.all_ratings_submitted
                  ? 'Finalize round results'
                  : 'All participants must submit ratings before finalizing'}
              >
                <Check size={16} className="mr-2" />
                Finalize Round Results
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="status" className="mt-4">
          <DetailedRatingStatusView />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdjudicatorView;